import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, Plus, X, Save, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

// Sun=0 ... Sat=6 (matches JS Date.getDay())
const WEEKDAYS = [
  { i: 1, short: "Mon", full: "Monday" },
  { i: 2, short: "Tue", full: "Tuesday" },
  { i: 3, short: "Wed", full: "Wednesday" },
  { i: 4, short: "Thu", full: "Thursday" },
  { i: 5, short: "Fri", full: "Friday" },
  { i: 6, short: "Sat", full: "Saturday" },
  { i: 0, short: "Sun", full: "Sunday" },
];

// Reasonable defaults for the slot picker (used as pre-populated chips).
const SUGGESTED_SLOTS = [
  "9:00 AM", "10:00 AM", "11:30 AM", "1:00 PM",
  "2:30 PM", "4:00 PM", "5:30 PM", "7:00 PM",
];

const SLOT_RE = /^\d{1,2}:\d{2}\s?(AM|PM)$/i;

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromISO(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function prettyISO(s) {
  return fromISO(s).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export default function AvailabilityCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5, 6]);
  const [slots, setSlots] = useState(["10:00 AM", "11:30 AM", "1:00 PM", "2:30 PM", "4:00 PM", "5:30 PM"]);
  const [blackouts, setBlackouts] = useState([]);
  const [customSlot, setCustomSlot] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/visit-availability");
        setWeekdays(data.weekdays || []);
        setSlots(data.time_slots || []);
        setBlackouts(data.blackout_dates || []);
      } catch {
        // keep defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleWd = (i) => {
    setWeekdays((wd) => (wd.includes(i) ? wd.filter((x) => x !== i) : [...wd, i].sort((a, b) => a - b)));
  };

  const toggleSlot = (s) => {
    setSlots((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const addCustomSlot = () => {
    const t = customSlot.trim().toUpperCase().replace(/\s+/g, " ");
    if (!SLOT_RE.test(t)) {
      toast.error("Format must be like 9:00 AM");
      return;
    }
    if (slots.includes(t)) {
      toast.error("Slot already added");
      return;
    }
    setSlots((cur) => [...cur, t]);
    setCustomSlot("");
  };

  const addBlackout = (d) => {
    if (!d) return;
    const iso = toISODate(d);
    if (blackouts.includes(iso)) return;
    setBlackouts((b) => [...b, iso].sort());
  };

  const removeBlackout = (iso) => {
    setBlackouts((b) => b.filter((x) => x !== iso));
  };

  const save = async () => {
    if (weekdays.length === 0) { toast.error("Pick at least one weekday"); return; }
    if (slots.length === 0) { toast.error("Pick at least one time slot"); return; }
    setSaving(true);
    try {
      await api.put("/me/visit-availability", {
        weekdays,
        time_slots: slots,
        blackout_dates: blackouts,
      });
      toast.success("Site-visit availability saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save availability");
    } finally {
      setSaving(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 180);

  if (loading) {
    return (
      <div className="bg-white border border-sg-line rounded-3xl p-7 flex items-center gap-2 text-sg-mute" data-testid="availability-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading availability…
      </div>
    );
  }

  return (
    <div className="bg-white border border-sg-line rounded-3xl p-7 space-y-6" data-testid="availability-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
          <CalendarIcon className="w-4 h-4" />
        </div>
        <div>
          <div className="overline text-sg-gold-deep">SITE-VISIT AVAILABILITY</div>
          <h3 className="font-display text-2xl text-sg-navy leading-tight">
            When can visitors <span className="font-serif-italic text-sg-gold-deep">book you?</span>
          </h3>
        </div>
      </div>
      <p className="text-sm text-sg-mute -mt-3">
        The InProp AI booking calendar on your branded portal only shows dates + slots you enable here.
      </p>

      {/* Weekday toggles */}
      <div>
        <div className="overline text-sg-gold-deep mb-2">DAYS YOU&apos;RE AVAILABLE</div>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((w) => {
            const on = weekdays.includes(w.i);
            return (
              <button
                key={w.i}
                type="button"
                onClick={() => toggleWd(w.i)}
                className={`h-10 px-4 rounded-full border text-xs font-semibold uppercase tracking-wider transition-colors ${
                  on ? "bg-sg-navy border-sg-navy text-white" : "bg-white border-sg-line text-sg-mute hover:border-sg-navy"
                }`}
                data-testid={`avail-wd-${w.short.toLowerCase()}`}
              >
                {w.short}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      <div>
        <div className="overline text-sg-gold-deep mb-2">TIME SLOTS YOU ATTEND</div>
        <div className="flex flex-wrap gap-2">
          {[...new Set([...SUGGESTED_SLOTS, ...slots])].sort(compareSlots).map((s) => {
            const on = slots.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSlot(s)}
                className={`h-9 px-3 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  on ? "bg-sg-gold/20 border-sg-gold text-sg-navy" : "bg-white border-sg-line text-sg-mute hover:border-sg-gold"
                }`}
                data-testid={`avail-slot-${s.replace(/[:\s]/g, "").toLowerCase()}`}
              >
                <Clock className="w-3 h-3" />
                {s}
                {on && <X className="w-3 h-3 opacity-60" />}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input
            type="text"
            value={customSlot}
            onChange={(e) => setCustomSlot(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSlot(); } }}
            placeholder="Add custom slot, e.g. 8:30 AM"
            className="text-sm bg-white border border-sg-line rounded-full px-4 py-2 flex-1 max-w-xs focus:outline-none focus:border-sg-gold-deep"
            data-testid="avail-slot-custom-input"
          />
          <button
            type="button"
            onClick={addCustomSlot}
            className="h-9 px-3 rounded-full border border-sg-line text-sg-navy text-xs font-semibold flex items-center gap-1 hover:border-sg-navy"
            data-testid="avail-slot-add"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
      </div>

      {/* Blackout dates */}
      <div>
        <div className="overline text-sg-gold-deep mb-2">BLACKOUT DATES (VACATIONS / FESTIVALS)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="bg-sg-cream-soft/40 border border-sg-line rounded-2xl p-2 max-w-fit">
            <Calendar
              mode="single"
              onSelect={addBlackout}
              disabled={(d) => d < today || d > maxDate}
              data-testid="avail-blackout-picker"
            />
          </div>
          <div className="space-y-2 min-h-[60px]">
            {blackouts.length === 0 ? (
              <div className="text-xs text-sg-mute italic">No blackout dates. Pick a date on the calendar to add one.</div>
            ) : (
              blackouts.map((iso) => (
                <div
                  key={iso}
                  className="flex items-center justify-between bg-sg-cream-soft/50 border border-sg-line rounded-lg px-3 py-2"
                  data-testid={`avail-blackout-${iso}`}
                >
                  <span className="text-sm text-sg-navy">{prettyISO(iso)}</span>
                  <button
                    type="button"
                    onClick={() => removeBlackout(iso)}
                    className="text-sg-mute hover:text-red-600"
                    aria-label="Remove blackout"
                    data-testid={`avail-blackout-remove-${iso}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="pt-2 flex items-center justify-end gap-3 border-t border-sg-line">
        <div className="text-[11px] text-sg-mute mr-auto">
          Preview: <span className="text-sg-navy font-medium">{weekdays.length}</span> day{weekdays.length === 1 ? "" : "s"} · <span className="text-sg-navy font-medium">{slots.length}</span> slot{slots.length === 1 ? "" : "s"} · <span className="text-sg-navy font-medium">{blackouts.length}</span> blackout{blackouts.length === 1 ? "" : "s"}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="h-10 px-5 rounded-full bg-sg-navy hover:bg-sg-navy/90 text-white text-xs font-semibold uppercase tracking-[0.14em] flex items-center gap-2 disabled:opacity-60"
          data-testid="avail-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save availability
        </button>
      </div>
    </div>
  );
}

function compareSlots(a, b) {
  return slotMinutes(a) - slotMinutes(b);
}
function slotMinutes(s) {
  const m = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i.exec(s);
  if (!m) return 0;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + parseInt(m[2], 10);
}
